import React, { lazy, Suspense, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import StoreProvider from "./store/StoreProvider";

import { Loading } from "./components/shared/Loading";
import LoginForm from "./components/LoginForm";
import Unauthorized from "./views/Unauthorized";
import UserProfile from "./components/UserProfile";
import { AllRoutes } from "./routes";
import {
  decryptPermissions,
  isPermissionExists,
} from "./utils/permissionsHelpers";
import { ROUTE_SCREENS } from "./routes/route-screens";

// ErrorBoundary Component to catch errors during lazy loading
function ErrorBoundary({ fallback, children }) {
  return <Suspense fallback={fallback}>{children}</Suspense>;
}

// Higher-order component for authentication
function Authenticate({ children }) {
  const { isLoggedIn } = useSelector((state) => state.auth);

  // Check if the path includes '/auth' and if not logged in, allow access for token validation
  const isAuthPath = window.location.pathname.startsWith("/auth");
  console.log(isAuthPath);
  // Only redirect to login if the user isn't logged in and it's not an auth path
  return isLoggedIn || isAuthPath ? children : <Navigate to="/login" />;
}

// Function to check if a path is allowed based on permissions
function isPathAllowed(pathToCheck, routes) {
  const checkPath = (path, routes) => {
    for (const route of routes) {
      const isPathMatched =
        !!route.path &&
        (route.path === path ||
          (route.path.includes(":") &&
            path.startsWith(route.path.split(":")[0])));

      if (isPathMatched) return true;

      if (route.childModules && checkPath(path, route.childModules)) {
        return true;
      }
    }
    return false;
  };

  return checkPath(pathToCheck, routes);
}

// Component to protect routes based on user permissions
const ProtectedRoute = ({ children, route }) => {
  const { isLoggedIn } = useSelector((state) => state.auth);
  const isAuthPath = route.path?.includes("auth");

  // Bypass login check for paths including '/auth' for token-based access
  if (!isLoggedIn && !isAuthPath) {
    return <Navigate to="/login" replace />;
  }

  let permissions = [];
  if (isPermissionExists()) {
    permissions = decryptPermissions();
  }

  const hasPathPermission =
    isPathAllowed(route?.path ?? "", permissions) ||
    route.code.toLowerCase().includes("dashboard");

  // Redirect unauthorized users if they lack permission and it's not an auth path
  if (!isAuthPath && !hasPathPermission) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children || <Outlet />;
};

// AppRoutes Component to handle the application's routing
const AppRoutes = () => {
  const routes = useMemo(() => {
    const getRoutesWithPath = (routes) => {
      let result = [];

      for (const route of routes) {
        if (route.path) {
          result.push(route);
        }

        if (route.childModules && route.childModules.length > 0) {
          const childObjectsWithPath = getRoutesWithPath(route.childModules);
          result = result.concat(childObjectsWithPath);
        }
      }

      return result;
    };

    return getRoutesWithPath(AllRoutes);
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "ACCESS_TOKEN") {
        const accessToken = event.data.accessToken;

        localStorage.setItem("token", JSON.stringify(accessToken));
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/*" element={<Outlet />}>
          <Route path="login" element={<LoginForm />} />
          <Route
            path="profile"
            element={
              <Authenticate>
                <UserProfile />
              </Authenticate>
            }
          />
          <Route path="unauthorized" element={<Unauthorized />} />
          {routes.map((route) => {
            if (!ROUTE_SCREENS[route?.code]) return null;

            const Component = ROUTE_SCREENS[route?.code];

            return (
              <Route
                key={route.path}
                element={<ProtectedRoute route={route} />}
              >
                <Route
                  path={route.path}
                  element={
                    <ErrorBoundary
                      fallback={
                        <Loading
                          fullScreen
                          open={true}
                          message={"Loading..."}
                        />
                      }
                    >
                      <Component />
                    </ErrorBoundary>
                  }
                />
              </Route>
            );
          })}
          <Route path="*" element={<Unauthorized />} />
        </Route>
      </Routes>
    </Router>
  );
};

const darkTheme = createTheme({
  palette: {
    primary: {
      main: "#85276b",
    },
    secondary: {
      main: "#2d7558",
    },
    default: {
      main: "#ffff",
      contrastText: "#000000",
    },
    mode: "dark",
  },
});

// Main App Component
function App() {
  return (
    <StoreProvider>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Suspense
          fallback={<Loading fullScreen open={true} message={"Loading..."} />}
        >
          <AppRoutes />
        </Suspense>
      </ThemeProvider>
    </StoreProvider>
  );
}

export default App;
